/**
 * AI Engine Error Handler
 * 
 * Centralized error handling for AI Engine operations with Sentry integration.
 * Extends the existing error handling pattern from Supabase error handler.
 */

import { AxiosError } from 'axios';
import { captureException, addBreadcrumb } from '../../lib/sentry';
import { getErrorMessageForStatus, getErrorMessageForCode, AI_ERROR_MESSAGES } from './errorMessages';

/**
 * Custom error class for AI Engine errors with user-friendly messages
 */
export class AIEngineError extends Error {
  public readonly userMessage: string;
  /** The Axios error behind it; absent for the streaming request, which uses fetch. */
  public readonly originalError?: AxiosError;
  public readonly statusCode?: number;
  public readonly errorCode?: string;

  constructor(
    userMessage: string,
    originalError?: AxiosError,
    statusCode?: number,
    errorCode?: string
  ) {
    super(userMessage);
    this.name = 'AIEngineError';
    this.userMessage = userMessage;
    this.originalError = originalError;
    this.statusCode = statusCode;
    this.errorCode = errorCode;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AIEngineError);
    }
  }
}

/**
 * Transform Axios error into user-friendly AIEngineError
 *
 * @param error - Axios error from AI Engine request
 * @param context - Additional context for debugging
 * @returns AIEngineError with user-friendly message
 */
export function handleAIEngineError(
  error: AxiosError,
  context?: {
    operation?: string;
    url?: string;
    extra?: Record<string, any>;
  }
): AIEngineError {
  const status = error.response?.status;
  const code = error.code;
  const url = error.config?.url || context?.url;

  // Extract backend correlation IDs from error response headers.
  // These are always echoed by RequestIDMiddleware and allow matching
  // frontend error reports to exact backend log lines for RCA.
  const requestId =
    error.response?.headers?.['x-request-id'] ??
    (context?.extra?.['requestId'] as string | undefined);
  const traceId =
    error.response?.headers?.['x-trace-id'] ??
    (context?.extra?.['traceId'] as string | undefined);

  // Determine user-friendly message
  let userMessage: string;

  // Special handling for 404 on API endpoints - likely means service is paused/down
  if (status === 404 && url?.includes('/api/')) {
    userMessage = 'AI service is currently paused or unavailable. Please contact your administrator or try again later.';
  } else if (status) {
    userMessage = getErrorMessageForStatus(status);
  } else if (code) {
    userMessage = getErrorMessageForCode(code);
  } else {
    userMessage = AI_ERROR_MESSAGES.UNKNOWN_ERROR;
  }

  // Add breadcrumb for all errors — include correlation IDs for RCA
  addBreadcrumb(
    `AI Engine Error: ${status || code || 'Unknown'}`,
    'http',
    'error',
    {
      status,
      code,
      url,
      operation: context?.operation,
      requestId,
      traceId,
    }
  );

  // Structured ERROR log with correlation IDs so console output
  // can be mapped to specific backend trace_id / request_id log lines.
  console.error('[AI Engine Error] ❌', {
    userMessage,
    status,
    code,
    url,
    responseData: error.response?.data,
    // Correlation IDs — search these in backend logs for full RCA
    requestId,
    traceId,
    context,
  });

  // Report to Sentry based on error type
  const shouldReport = shouldReportToSentry(status, code);
  const sampleRate = getSampleRate(status, code);

  if (shouldReport && Math.random() < sampleRate) {
    captureException(error, {
      tags: {
        errorType: 'ai_engine_error',
        service: 'ai_engine',
        statusCode: status?.toString() || 'unknown',
        errorCode: code || 'unknown',
      },
      extra: {
        userMessage,
        url,
        responseData: error.response?.data,
        // Correlation IDs for Sentry ↔ backend log linking
        requestId,
        traceId,
        ...context,
      },
      level: getErrorLevel(status, code),
    });
  }

  return new AIEngineError(userMessage, error, status, code);
}

/**
 * Determine if error should be reported to Sentry
 */
function shouldReportToSentry(status?: number, code?: string): boolean {
  // Don't report expected client errors
  if (status === 400 || status === 422) {
    return false;
  }

  // Don't report 404 on API endpoints - this is expected when service is paused
  if (status === 404) {
    return false;
  }

  // Report all server errors and service unavailable
  if (status && status >= 500) {
    return true;
  }

  // Report authentication errors (might indicate token issues)
  if (status === 401 || status === 403) {
    return true;
  }

  // Report network errors
  if (code === 'ECONNREFUSED' || code === 'ENOTFOUND' || code === 'ENETUNREACH') {
    return true;
  }

  // Report rate limits and timeouts (sampled)
  if (status === 429 || code === 'ECONNABORTED' || code === 'ETIMEDOUT') {
    return true;
  }

  return false;
}

/**
 * Get sample rate for Sentry reporting (to avoid quota exhaustion)
 */
function getSampleRate(status?: number, code?: string): number {
  // Sample rate limits and timeouts at 10%
  if (status === 429 || code === 'ECONNABORTED' || code === 'ETIMEDOUT') {
    return 0.1;
  }

  // Report all other errors at 100%
  return 1.0;
}

/**
 * Get Sentry error level based on error type
 */
function getErrorLevel(status?: number, code?: string): 'error' | 'warning' | 'info' {
  // Server errors are critical
  if (status && status >= 500) {
    return 'error';
  }

  // Rate limits and timeouts are warnings
  if (status === 429 || code === 'ECONNABORTED' || code === 'ETIMEDOUT') {
    return 'warning';
  }

  // Authentication errors are errors
  if (status === 401 || status === 403) {
    return 'error';
  }

  // Everything else is a warning
  return 'warning';
}

